import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import {
    AppState,
    AuthService,
    BlockSessionModalComponent,
    CacheService,
    CardType,
    CategoryType,
    PageService,
    PageType,
    PillarType,
    SettingsService,
} from '@gallaghercp/core-frontend';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, of, switchMap } from 'rxjs';
import { map, take } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class ClientService implements CanActivate {
    static PIDS_BENEFITS: number[] = [2];

    static PILLARS = [''];
    static PIDS_LIFE_JOURNEYS: number[] = [];
    DEFAULT = {
        name: 'Vanilla',
        login: 'Welcome back',
        welcome: 'Enter your credentials to access your account.',
        support: 'Need help signing in?',
        uid: 'Employee ID',
        uidHelper: false,
        dobFormat: 'DD/MM/YYYY',
    };

    static LIFE_JOURNEYS = 'life-journeys';
    static ARTICLES = 'articles';
    static BENEFITS = 'benefits';
    static POPULAR_BENEFITS = 'popular-benefits';
    static LATEST_ARTICLES = 'latest-articles';
    static RELATED_CONTENT = 'related-content';
    static KEYWORD_CONTENT = 'keyword-content';
    static DOWNLOAD = 'download';
    static GENERIC_PAGE = 'page';

    provider: any;

    private sessionTimeoutModal?: any;

    constructor(
        protected auth: AuthService,
        protected router: Router,
        protected pages: PageService,
        protected cache: CacheService,
        protected settings: SettingsService,
        protected modalService: BsModalService,
    ) {
        this.auth.sessionEmit.subscribe(ping => {
            if (!this.sessionTimeoutModal) {
                this.showSessionTimeoutModal(ping);
            }
        });
    }

    canActivate(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): Observable<boolean> | Promise<boolean> | boolean {
        this.auth.setRedirectUrl(state.url);
        if (!this.auth.authResumed) {
            this.auth.resume();
        }
        return this.auth.isResumed.pipe(
            take(1),
            switchMap(() => {
                if (this.auth.isAuthenticated()) {
                    return of(true);
                }
                return this.getAccessType().pipe(
                    map((type: any) => {
                        if (type === 'public' && state.url !== '/profile') {
                            return true;
                        }
                        this.router.navigate(['/login']);
                        return false;
                    }),
                );
            }),
        );
    }

    getConfiguration() {
        this.provider = this.auth.serviceProvider;
        return this.provider && this.provider.configuration ? this.provider.configuration : {};
    }

    getAccessType(): Observable<AppState> {
        return this.settings.getAuthenticationType().pipe(
            map((authenticationType: any) => {
                if (authenticationType !== undefined) {
                    return authenticationType;
                }
                return this.getConfiguration().accessType || 'individual';
            }),
        );
    }

    getLogoURL() {
        return this.settings.getLogo().pipe(
            map((logoUrl: any) => {
                if (this.getConfiguration().logo) {
                    return this.getConfiguration().logo;
                }
                if (logoUrl !== undefined) {
                    return logoUrl;
                }
                return 'https://storage.googleapis.com/archipelago-assets/Gallagher_HorizontalLarge-3D.png';
            }),
        );
    }

    getLogoTransparentURL() {
        return this.settings.getTransparentLogo().pipe(
            map((logoUrl: any) => {
                if (this.getConfiguration().logoTransparent) {
                    return this.getConfiguration().logoTransparent;
                }
                if (logoUrl !== undefined) {
                    return logoUrl;
                }
                return 'https://storage.googleapis.com/archipelago-assets/Gallagher_HorizontalLarge-3D-Reverse.png';
            }),
        );
    }

    getLogoInvertedURL() {
        return this.settings.getInvertedLogo().pipe(
            map((logoUrl: any) => {
                if (logoUrl !== undefined) {
                    return logoUrl;
                }
                return this.getConfiguration().logoInverted || this.getLogoURL();
            }),
        );
    }

    getSiteName() {
        return this.getConfiguration().name || this.DEFAULT.name;
    }

    getLoginTitle() {
        return this.getConfiguration().auth && this.getConfiguration().auth.login
            ? this.getConfiguration().auth.login.title
            : this.DEFAULT.login || this.DEFAULT.login;
    }

    getLoginWelcomeText() {
        return this.getConfiguration().auth && this.getConfiguration().auth.login
            ? this.getConfiguration().auth.login.welcomeText
            : this.DEFAULT.welcome || this.DEFAULT.welcome;
    }

    getUID() {
        return this.getConfiguration().auth ? this.getConfiguration().auth.UID : this.DEFAULT.uid || this.DEFAULT.uid;
    }

    getUIDHelper() {
        return this.getConfiguration().auth
            ? this.getConfiguration().auth.UIDHelper
            : this.DEFAULT.uidHelper || this.DEFAULT.uidHelper;
    }

    getDOBFormat() {
        return this.getConfiguration().auth && this.getConfiguration().auth.DOBFormat
            ? this.getConfiguration().auth.DOBFormat.toUpperCase()
            : this.DEFAULT.dobFormat || this.DEFAULT.dobFormat;
    }

    getBenefitContent() {
        return this.pages.getOneThroughCache('benefit-content', this.pages.getCurrentPage().id);
    }

    getUserData() {
        return this.auth.getUser() ? this.auth.getUser().data : [];
    }

    getUserNotifications(clear?: boolean) {
        return clear
            ? this.cache.getListThroughCache(
                  'user-notifications',
                  { interacted: false },
                  {},
                  Date.now() as unknown as string,
              )
            : this.cache.getListThroughCache('user-notifications', { interacted: false });
    }

    showSessionTimeoutModal(ping: number): void {
        this.sessionTimeoutModal = this.modalService.show(BlockSessionModalComponent, {
            backdrop: 'static',
            keyboard: false,
            class: 'modal-md modal--session text-center modal-dialog-centered',
            animated: true,
            initialState: {
                ping,
            },
        });
    }

    hideSessionModals(): void {
        if (this.sessionTimeoutModal) {
            this.sessionTimeoutModal.hide();
        }
    }

    getPageCategories() {
        return this.pages.getListThroughCache('page-categories').pipe(
            map((categories: any[any]) => {
                let allPages: any[] = [];
                categories.forEach((category: { pageCategories: [] }) => {
                    if (category.pageCategories) {
                        category.pageCategories.forEach((page: { pageId: number }) => {
                            if (allPages.filter((e: { id: number }) => e.id === page.pageId).length === 0) {
                                allPages.push(page);
                            }
                        });
                    }
                });
                if (categories.filter((e: { name: string }) => e.name === 'All').length === 0) {
                    categories.unshift({ id: 0, name: 'All', pageCategories: allPages });
                }
                return categories;
            }),
        );
    }

    attachCategoryNames(cards: CardType[]): CardType[] {
        return this.getPageCategories().pipe(
            map((pageCategories: CategoryType[]) => {
                cards.map((card: CardType) => {
                    (card.categories ?? []).forEach((cardCategory: any) => {
                        pageCategories.forEach((pageCategory: CategoryType) => {
                            if (pageCategory.id === parseInt(String(cardCategory))) {
                                card.categoryNames = [...(card.categoryNames ?? []), ...[pageCategory.name]];
                            }
                        });
                    });
                    return card;
                });
                return cards;
            }),
        );
    }

    getPillars() {
        return this.cache.getListThroughCache('page-categories').pipe(
            map((categories: any[any]) => {
                let allPages: any[] = [];
                const pillars = categories.filter((pillar: PillarType) => {
                    return ClientService.PILLARS.includes(pillar.name);
                });
                pillars.forEach((category: { pageCategories: [] }) => {
                    if (category.pageCategories) {
                        category.pageCategories.forEach((page: { url: string }) => {
                            if (allPages.filter((e: { url: string }) => e.url === page.url).length === 0) {
                                allPages.push(page);
                            }
                        });
                    }
                });
                // if (pillars.filter((e: { name: string; }) => e.name === 'All').length === 0) {
                //     pillars.push({id: 0, name: 'All', pageCategories: allPages})
                // }
                return pillars;
            }),
        );
    }

    isBenefitsMainLandingPage(): boolean {
        return this.pages.getCurrentPage() && ClientService.PIDS_BENEFITS.includes(this.pages.getCurrentPage().id);
    }

    getLifeJourneysRelated(lifeMoment: PageType, ignoreId: number, limit?: number): Observable<CardType[]> {
        return this.cache.getListThroughCache('page-categories').pipe(
            map((categories: CategoryType[]) => {
                let relevantCategory: any | false = false;
                categories.forEach((category: CategoryType) => {
                    if (category.name.toLowerCase() === (lifeMoment.meta?.momentTag ?? lifeMoment.name)) {
                        relevantCategory = category;
                    }
                });
                return relevantCategory;
            }),
            switchMap((category: CategoryType) => {
                return this.pages
                    .getPages(undefined, {
                        q: JSON.stringify({
                            parentId: {
                                _in: ClientService.PIDS_BENEFITS,
                            },
                            categories: {
                                categoryId: {
                                    _in: [category.id],
                                },
                            },
                        }),
                        limit: (limit ?? 4) + 1, // Overhead save on filtering ignoreId rather than API query
                    })
                    .pipe(
                        map((pages: PageType[]) => {
                            return pages.filter((page: PageType) => {
                                return page.id !== ignoreId;
                            });
                        }),
                    );
            }),
        );
    }

    isTouchDevice() {
        return (
            'ontouchstart' in window ||
            navigator.maxTouchPoints > 0 ||
            // @ts-ignore
            navigator['msMaxTouchPoints'] > 0
        );
    }
}
